import { useCallback, useEffect, useState } from "react";
import { Form, InputNumber, Switch, Button, Select } from "antd";
import { toast } from "react-toastify";
import { getRequest, postRequest, putRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import "./PaymentPlanConfiguration.scss";

const digitalMemberMonthlyDefaults = {
  starter: 0,
  essential: 0,
  essentialPlus: 0,
};

const digitalMemberYearlyDefaults = {
  starter: 0,
  essential: 0,
  essentialPlus: 0,
};

const fullMemberMonthlyDefaults = {
  starter: 0,
  essential: 0,
  essentialPlus: 0,
};

const fullMemberYearlyDefaults = {
  starter: 0,
  essential: 0,
  essentialPlus: 0,
};

const MEMBER_KEY_BY_TYPE = {
  "FULL MEMBER": "fullMember",
  "DIGITAL MEMBER": "digitalMember",
};

const CYCLE_KEY_BY_TYPE = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

const PLAN_FIELD_BY_KEY = {
  STARTER: "starter",
  ESSENTIAL: "essential",
  ESSENTIALS: "essential",
  ESSENTIAL_PLUS: "essentialPlus",
  ESSENTIALS_PLUS: "essentialPlus",
};

const MEMBER_KEYS = ["digitalMember", "fullMember"];
const CYCLE_KEYS = ["monthly", "yearly"];
const PRICE_FIELDS = ["starter", "essential", "essentialPlus"];

const DEFAULT_APPLICATION_FEE = 0;

const parseNumericInput = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).replace(/[^\d.]/g, "");
};

const blockNonNumericKey = (event) => {
  if (event.ctrlKey || event.metaKey) {
    return;
  }
  const { key } = event;
  if (key.length === 1 && !/[\d.]/.test(key)) {
    event.preventDefault();
  }
};

const numericInputProps = {
  parser: parseNumericInput,
  onKeyDown: blockNonNumericKey,
};

const extractPlanRecord = (payload) => {
  if (Array.isArray(payload)) {
    return payload[0] || null;
  }
  return payload || null;
};

const normalizeUpdatedCycle = (cycle) => {
  const normalized = String(cycle || "").toUpperCase();
  return normalized === "YEARLY" ? "YEARLY" : "MONTHLY";
};

const createEmptyPlanIds = () => ({
  digitalMember: {
    monthly: { starter: null, essential: null, essentialPlus: null },
    yearly: { starter: null, essential: null, essentialPlus: null },
  },
  fullMember: {
    monthly: { starter: null, essential: null, essentialPlus: null },
    yearly: { starter: null, essential: null, essentialPlus: null },
  },
});

const createDefaultPaymentPlans = () => ({
  digitalMember: {
    monthly: { ...digitalMemberMonthlyDefaults },
    yearly: { ...digitalMemberYearlyDefaults },
  },
  fullMember: {
    monthly: { ...fullMemberMonthlyDefaults },
    yearly: { ...fullMemberYearlyDefaults },
  },
});

const clonePaymentPlans = (plans) => JSON.parse(JSON.stringify(plans));

const createDefaultMemberApplicationFees = () => ({
  fullMember: {
    starter: DEFAULT_APPLICATION_FEE,
    essential: DEFAULT_APPLICATION_FEE,
    essentialPlus: DEFAULT_APPLICATION_FEE,
  },
  digitalMember: {
    starter: DEFAULT_APPLICATION_FEE,
    essential: DEFAULT_APPLICATION_FEE,
    essentialPlus: DEFAULT_APPLICATION_FEE,
  },
});

const cloneMemberApplicationFees = (fees) =>
  JSON.parse(JSON.stringify(fees));

const pickNumericRecordValue = (planRecord, firstPlan, key) => {
  const value = planRecord?.[key] ?? firstPlan?.[key];
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  return Number(value);
};

const mapMemberApplicationFeesFromRecord = (planRecord = {}, firstPlan = {}) => {
  const fees = createDefaultMemberApplicationFees();
  const fullMemberPlanInput =
    planRecord?.fullMemberPlanInput ?? firstPlan?.fullMemberPlanInput;
  const digitalMemberPlanInput =
    planRecord?.digitalMemberPlanInput ?? firstPlan?.digitalMemberPlanInput;

  fees.fullMember.starter =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "fullMemberStarterApplicationFee"
    ) ??
    (fullMemberPlanInput?.starterApplicationFee !== undefined
      ? Number(fullMemberPlanInput.starterApplicationFee)
      : fees.fullMember.starter);
  fees.fullMember.essential =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "fullMemberEssentialApplicationFee"
    ) ??
    (fullMemberPlanInput?.essentialApplicationFee !== undefined
      ? Number(fullMemberPlanInput.essentialApplicationFee)
      : fees.fullMember.essential);
  fees.fullMember.essentialPlus =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "fullMemberEssentialPlusApplicationFee"
    ) ??
    (fullMemberPlanInput?.essentialPlusApplicationFee !== undefined
      ? Number(fullMemberPlanInput.essentialPlusApplicationFee)
      : fees.fullMember.essentialPlus);

  fees.digitalMember.starter =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "digitalMemberStarterApplicationFee"
    ) ??
    (digitalMemberPlanInput?.starterApplicationFee !== undefined
      ? Number(digitalMemberPlanInput.starterApplicationFee)
      : fees.digitalMember.starter);
  fees.digitalMember.essential =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "digitalMemberEssentialApplicationFee"
    ) ??
    (digitalMemberPlanInput?.essentialApplicationFee !== undefined
      ? Number(digitalMemberPlanInput.essentialApplicationFee)
      : fees.digitalMember.essential);
  fees.digitalMember.essentialPlus =
    pickNumericRecordValue(
      planRecord,
      firstPlan,
      "digitalMemberEssentialPlusApplicationFee"
    ) ??
    (digitalMemberPlanInput?.essentialPlusApplicationFee !== undefined
      ? Number(digitalMemberPlanInput.essentialPlusApplicationFee)
      : fees.digitalMember.essentialPlus);

  return fees;
};

const memberApplicationFeesChanged = (currentFees, originalFees) =>
  MEMBER_KEYS.some((memberKey) =>
    PRICE_FIELDS.some(
      (field) =>
        Number(currentFees?.[memberKey]?.[field] ?? 0) !==
        Number(originalFees?.[memberKey]?.[field] ?? 0)
    )
  );

const buildMemberApplicationFeePayload = (fees) => ({
  fullMemberStarterApplicationFee: Number(fees.fullMember.starter),
  fullMemberEssentialApplicationFee: Number(fees.fullMember.essential),
  fullMemberEssentialPlusApplicationFee: Number(
    fees.fullMember.essentialPlus
  ),
  digitalMemberStarterApplicationFee: Number(fees.digitalMember.starter),
  digitalMemberEssentialApplicationFee: Number(fees.digitalMember.essential),
  digitalMemberEssentialPlusApplicationFee: Number(
    fees.digitalMember.essentialPlus
  ),
});

const mapOrganizationPricePlanList = (planList = []) => {
  const mappedPlans = createDefaultPaymentPlans();
  const planIds = createEmptyPlanIds();

  planList.forEach((plan) => {
    const memberKey = MEMBER_KEY_BY_TYPE[plan?.memberType];
    const cycleKey = CYCLE_KEY_BY_TYPE[plan?.cycle];
    if (!memberKey || !cycleKey || !plan?.planPrices) {
      return;
    }

    Object.entries(plan.planPrices).forEach(([priceKey, priceValue]) => {
      const field = PLAN_FIELD_BY_KEY[priceKey];
      if (
        !field ||
        priceValue === null ||
        priceValue === undefined ||
        priceValue === ""
      ) {
        return;
      }
      mappedPlans[memberKey][cycleKey][field] = Number(priceValue);
      if (plan.planId) {
        planIds[memberKey][cycleKey][field] = plan.planId;
      }
    });
  });

  return { mappedPlans, planIds };
};

const buildUpdatePlanInput = (currentPlans, originalPlans, planIds) => {
  const planInput = [];

  MEMBER_KEYS.forEach((memberKey) => {
    CYCLE_KEYS.forEach((cycleKey) => {
      PRICE_FIELDS.forEach((field) => {
        const partnerPlanId = planIds?.[memberKey]?.[cycleKey]?.[field];
        const newPlanPrice = currentPlans?.[memberKey]?.[cycleKey]?.[field];
        const originalPrice = originalPlans?.[memberKey]?.[cycleKey]?.[field];

        if (
          !partnerPlanId ||
          newPlanPrice === null ||
          newPlanPrice === undefined ||
          Number(newPlanPrice) === Number(originalPrice)
        ) {
          return;
        }

        planInput.push({
          partnerPlanId,
          newPlanPrice: Number(newPlanPrice),
        });
      });
    });
  });

  return planInput;
};

const PaymentPlanConfiguration = ({
  partnerId,
  countryId,
  disabled: paymentPlanDisabled = false,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [applicationFee, setApplicationFee] = useState(
    DEFAULT_APPLICATION_FEE
  );
  const [discountPercent, setDiscountPercent] = useState(0);
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [originalExtras, setOriginalExtras] = useState({
    applicationFee: DEFAULT_APPLICATION_FEE,
    discountPercent: 0,
    currency: undefined,
    digitalMemberUpdatedCycle: "MONTHLY",
    fullMemberUpdatedCycle: "MONTHLY",
  });
  const [planIds, setPlanIds] = useState(createEmptyPlanIds);
  const [originalPaymentPlans, setOriginalPaymentPlans] = useState(
    createDefaultPaymentPlans
  );

  const [paymentType, setPaymentType] = useState("MONTHLY");

  const [fullMemberPaymentType, setFullMemberPaymentType] =
    useState("MONTHLY");

  const [paymentPlans, setPaymentPlans] = useState(createDefaultPaymentPlans);
  const [memberApplicationFees, setMemberApplicationFees] = useState(
    createDefaultMemberApplicationFees
  );
  const [originalMemberApplicationFees, setOriginalMemberApplicationFees] =
    useState(createDefaultMemberApplicationFees);

  const syncFormValues = useCallback(
    (
      plans,
      digitalCycle = "MONTHLY",
      fullCycle = "MONTHLY",
      extras = {},
      applicationFees = createDefaultMemberApplicationFees()
    ) => {
      const digitalValues =
        plans.digitalMember[digitalCycle === "MONTHLY" ? "monthly" : "yearly"];
      const fullMemberValues =
        plans.fullMember[fullCycle === "MONTHLY" ? "monthly" : "yearly"];

      form.setFieldsValue({
        digitalMemberStarter: digitalValues.starter,
        digitalMemberEssential: digitalValues.essential,
        digitalMemberEssentialPlus: digitalValues.essentialPlus,
        fullMemberStarter: fullMemberValues.starter,
        fullMemberEssential: fullMemberValues.essential,
        fullMemberEssentialPlus: fullMemberValues.essentialPlus,
        applicationFee:
          extras.applicationFee ?? DEFAULT_APPLICATION_FEE,
        discountPercentage: extras.discountPercent ?? 0,
        currency: extras.currency,
        fullMemberStarterApplicationFee:
          applicationFees.fullMember.starter,
        fullMemberEssentialApplicationFee:
          applicationFees.fullMember.essential,
        fullMemberEssentialPlusApplicationFee:
          applicationFees.fullMember.essentialPlus,
        digitalMemberStarterApplicationFee:
          applicationFees.digitalMember.starter,
        digitalMemberEssentialApplicationFee:
          applicationFees.digitalMember.essential,
        digitalMemberEssentialPlusApplicationFee:
          applicationFees.digitalMember.essentialPlus,
      });
    },
    [form]
  );

  const fetchCurrencies = useCallback(async () => {
    if (!countryId) {
      setCurrencies([]);
      return;
    }

    try {
      setCurrenciesLoading(true);
      const response = await getRequest(
        userServiceApi.stripeSupportedCurrencies(countryId)
      );
      setCurrencies(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.data?.error || error?.data?.message || error);
      setCurrencies([]);
    } finally {
      setCurrenciesLoading(false);
    }
  }, [countryId]);

  const fetchPartnerPlans = useCallback(async () => {
    const currentPartnerId = partnerId || sessionStorage.getItem("partnerId");
    if (!currentPartnerId) {
      return;
    }

    try {
      const response = await getRequest(
        userServiceApi.getPartnerPlans(currentPartnerId)
      );
      const payload = response?.data;
      const planRecord = extractPlanRecord(payload);
      const planList = planRecord?.organizationPricePlanList || [];
      const firstPlan = planList[0] || {};
      const fetchedCurrency =
        planRecord?.currency ?? firstPlan.currency;

      if (!Array.isArray(planList) || planList.length === 0) {
        setIsUpdate(false);
        if (fetchedCurrency) {
          const extras = {
            applicationFee: DEFAULT_APPLICATION_FEE,
            discountPercent: 0,
            currency: fetchedCurrency,
          };
          setOriginalExtras(extras);
          form.setFieldValue("currency", fetchedCurrency);
        }
        return;
      }

      const { mappedPlans, planIds: fetchedPlanIds } =
        mapOrganizationPricePlanList(planList);
      const fetchedApplicationFee =
        planRecord?.applicationFee ??
        firstPlan.applicationFee ??
        DEFAULT_APPLICATION_FEE;
      const fetchedDiscountPercent =
        planRecord?.discountPercent ??
        planRecord?.discountPercentage ??
        firstPlan.discountPercent ??
        firstPlan.discountPercentage ??
        0;

      const fetchedDigitalCycle = normalizeUpdatedCycle(
        planRecord?.digitalMemberUpdatedCycle ??
          firstPlan.digitalMemberUpdatedCycle
      );
      const fetchedFullCycle = normalizeUpdatedCycle(
        planRecord?.fullMemberUpdatedCycle ?? firstPlan.fullMemberUpdatedCycle
      );

      const fetchedMemberApplicationFees = mapMemberApplicationFeesFromRecord(
        planRecord,
        firstPlan
      );

      const fetchedExtras = {
        applicationFee: fetchedApplicationFee,
        discountPercent: fetchedDiscountPercent,
        currency: fetchedCurrency,
        digitalMemberUpdatedCycle: fetchedDigitalCycle,
        fullMemberUpdatedCycle: fetchedFullCycle,
      };

      setPaymentPlans(mappedPlans);
      setOriginalPaymentPlans(clonePaymentPlans(mappedPlans));
      setPlanIds(fetchedPlanIds);
      setIsUpdate(true);
      setApplicationFee(fetchedApplicationFee);
      setDiscountPercent(fetchedDiscountPercent);
      setMemberApplicationFees(fetchedMemberApplicationFees);
      setOriginalMemberApplicationFees(
        cloneMemberApplicationFees(fetchedMemberApplicationFees)
      );
      setOriginalExtras(fetchedExtras);
      setPaymentType(fetchedDigitalCycle);
      setFullMemberPaymentType(fetchedFullCycle);
      syncFormValues(
        mappedPlans,
        fetchedDigitalCycle,
        fetchedFullCycle,
        fetchedExtras,
        fetchedMemberApplicationFees
      );
    } catch (error) {
      const status = error?.status;
      if (status && status !== 404) {
        toast.error(error?.data?.error || error?.data?.message || error);
      }
      setIsUpdate(false);
    }
  }, [partnerId, syncFormValues, form]);

  useEffect(() => {
    fetchPartnerPlans();
  }, [fetchPartnerPlans]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  useEffect(() => {
    if (!countryId || currencies.length === 0 || !originalExtras.currency) {
      return;
    }

    form.setFieldValue("currency", originalExtras.currency);
  }, [countryId, currencies, originalExtras.currency, form]);

  const handlePaymentTypeChange = (checked) => {
    const newPaymentType = checked ? "YEARLY" : "MONTHLY";

    setPaymentType(newPaymentType);

    const selectedValues =
      paymentPlans.digitalMember[
      newPaymentType === "MONTHLY"
        ? "monthly"
        : "yearly"
      ];

    form.setFieldsValue({
      digitalMemberStarter: selectedValues.starter,
      digitalMemberEssential: selectedValues.essential,
      digitalMemberEssentialPlus:
        selectedValues.essentialPlus,
    });
  };



  const handleFullMemberPaymentTypeChange = (checked) => {
    const newPaymentType = checked ? "YEARLY" : "MONTHLY";

    setFullMemberPaymentType(newPaymentType);

    const selectedValues =
      paymentPlans.fullMember[
      newPaymentType === "MONTHLY"
        ? "monthly"
        : "yearly"
      ];

    form.setFieldsValue({
      fullMemberStarter: selectedValues.starter,
      fullMemberEssential: selectedValues.essential,
      fullMemberEssentialPlus:
        selectedValues.essentialPlus,
    });
  };


  const handleDigitalMemberPlanChange = (
    field,
    value
  ) => {
    const cycle =
      paymentType === "MONTHLY"
        ? "monthly"
        : "yearly";

    setPaymentPlans((prev) => ({
      ...prev,

      digitalMember: {
        ...prev.digitalMember,

        [cycle]: {
          ...prev.digitalMember[cycle],
          [field]: value,
        },
      },
    }));
  };



  const handleMemberApplicationFeeChange = (memberKey, field, value) => {
    setMemberApplicationFees((prev) => ({
      ...prev,
      [memberKey]: {
        ...prev[memberKey],
        [field]: value,
      },
    }));
  };

  const handleFullMemberPlanChange = (
    field,
    value
  ) => {
    const cycle =
      fullMemberPaymentType === "MONTHLY"
        ? "monthly"
        : "yearly";

    setPaymentPlans((prev) => ({
      ...prev,

      fullMember: {
        ...prev.fullMember,

        [cycle]: {
          ...prev.fullMember[cycle],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (values) => {
    const currentPartnerId =
      partnerId || sessionStorage.getItem("partnerId");

    if (!currentPartnerId) {
      toast.error("Partner ID is missing");
      return;
    }

    const createPayload = {
      partnerId: currentPartnerId,
      fullMemberPlanInput: {
        member: "FULL MEMBER",
        starterApplicationFee: memberApplicationFees.fullMember.starter,
        essentialApplicationFee: memberApplicationFees.fullMember.essential,
        essentialPlusApplicationFee:
          memberApplicationFees.fullMember.essentialPlus,
        fullMemberPlanMonthly: {
          starterMonthlyPlanPrice:
            paymentPlans.fullMember.monthly.starter,
          essentialMonthlyPlanPrice:
            paymentPlans.fullMember.monthly.essential,
          essentialPlusMonthlyPlanPrice:
            paymentPlans.fullMember.monthly.essentialPlus,
          subscriptionCycle: "MONTHLY",
        },
        fullMemberPlanYearly: {
          starterYearlyPlanPrice:
            paymentPlans.fullMember.yearly.starter,
          essentialYearlyPlanPrice:
            paymentPlans.fullMember.yearly.essential,
          essentialPlusYearlyPlanPrice:
            paymentPlans.fullMember.yearly.essentialPlus,
          subscriptionCycle: "YEARLY",
        },
      },
      digitalMemberPlanInput: {
        member: "DIGITAL MEMBER",
        starterApplicationFee: memberApplicationFees.digitalMember.starter,
        essentialApplicationFee: memberApplicationFees.digitalMember.essential,
        essentialPlusApplicationFee:
          memberApplicationFees.digitalMember.essentialPlus,
        digitalMemberPlanMonthly: {
          starterMonthlyPlanPrice:
            paymentPlans.digitalMember.monthly.starter,
          essentialMonthlyPlanPrice:
            paymentPlans.digitalMember.monthly.essential,
          essentialPlusMonthlyPlanPrice:
            paymentPlans.digitalMember.monthly.essentialPlus,
          subscriptionCycle: "MONTHLY",
        },
        digitalMemberPlanYearly: {
          starterYearlyPlanPrice:
            paymentPlans.digitalMember.yearly.starter,
          essentialYearlyPlanPrice:
            paymentPlans.digitalMember.yearly.essential,
          essentialPlusYearlyPlanPrice:
            paymentPlans.digitalMember.yearly.essentialPlus,
          subscriptionCycle: "YEARLY",
        },
      },
      applicationFee,
      discountPercent,
      currency: values?.currency,
      digitalMemberUpdatedCycle: paymentType,
      fullMemberUpdatedCycle: fullMemberPaymentType,
      ...buildMemberApplicationFeePayload(memberApplicationFees),
    };

    try {
      setSubmitting(true);

      let response;
      if (isUpdate) {
        const planInput = buildUpdatePlanInput(
          paymentPlans,
          originalPaymentPlans,
          planIds
        );
        const hasCurrencyChange =
          values?.currency !== originalExtras.currency;
        const hasApplicationFeeChange =
          Number(applicationFee) !==
          Number(originalExtras.applicationFee);
        const hasDiscountChange =
          Number(discountPercent ?? 0) !==
          Number(originalExtras.discountPercent ?? 0);
        const hasDigitalCycleChange =
          paymentType !==
          normalizeUpdatedCycle(originalExtras.digitalMemberUpdatedCycle);
        const hasFullCycleChange =
          fullMemberPaymentType !==
          normalizeUpdatedCycle(originalExtras.fullMemberUpdatedCycle);
        const hasCycleChange = hasDigitalCycleChange || hasFullCycleChange;
        const hasMemberApplicationFeesChange = memberApplicationFeesChanged(
          memberApplicationFees,
          originalMemberApplicationFees
        );

        if (
          !planInput.length &&
          !hasCurrencyChange &&
          !hasApplicationFeeChange &&
          !hasDiscountChange &&
          !hasCycleChange &&
          !hasMemberApplicationFeesChange
        ) {
          return;
        }

        response = await putRequest(userServiceApi.updatePartnerPlans(), {
          partnerId: currentPartnerId,
          planInput,
          applicationFee,
          discountPercent: discountPercent ?? 0,
          currency: values?.currency,
          digitalMemberUpdatedCycle: paymentType,
          fullMemberUpdatedCycle: fullMemberPaymentType,
          ...buildMemberApplicationFeePayload(memberApplicationFees),
        });
      } else {
        response = await postRequest(
          userServiceApi.addPartnerPlans(),
          createPayload
        );
      }

      if (response) {
        toast.success(
          isUpdate
            ? "Partner payment plans updated successfully"
            : "Partner payment plans added successfully"
        );
        setIsUpdate(true);
        await fetchPartnerPlans();
      }
    } catch (error) {
      const message =
        error?.error || error?.message || error?.data?.error || error?.data?.message;
      if (message) {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };


  const handleCancel = () => {
    const restoredDigitalCycle = normalizeUpdatedCycle(
      originalExtras.digitalMemberUpdatedCycle
    );
    const restoredFullCycle = normalizeUpdatedCycle(
      originalExtras.fullMemberUpdatedCycle
    );

    setPaymentPlans(originalPaymentPlans);
    setApplicationFee(originalExtras.applicationFee);
    setDiscountPercent(originalExtras.discountPercent);
    setMemberApplicationFees(
      cloneMemberApplicationFees(originalMemberApplicationFees)
    );
    setPaymentType(restoredDigitalCycle);
    setFullMemberPaymentType(restoredFullCycle);
    syncFormValues(
      originalPaymentPlans,
      restoredDigitalCycle,
      restoredFullCycle,
      originalExtras,
      originalMemberApplicationFees
    );
  };

  // COMMON VALIDATION RULES
  const priceRules = (planName) => [
    {
      required: true,
      message: `Please enter ${planName} plan price.`,
    },
    {
      type: "number",
      message: "Please enter a valid number.",
    },
    {
      validator: (_, value) => {
        if (value < 0) {
          return Promise.reject(
            new Error(
              "Negative values are not allowed."
            )
          );
        }

        return Promise.resolve();
      },
    },
  ];


  const fieldDisabled = paymentPlanDisabled || submitting;

  return (
    <div
      className={`payment-plan-container${
        paymentPlanDisabled ? " payment-plan-container--disabled" : ""
      }`}
    >
      <div className="form-component">
        <Form
          form={form}
          onFinish={handleSubmit}
          autoComplete="off"
          initialValues={{
            // Digital Member
            digitalMemberStarter:
              digitalMemberMonthlyDefaults.starter,

            digitalMemberEssential:
              digitalMemberMonthlyDefaults.essential,

            digitalMemberEssentialPlus:
              digitalMemberMonthlyDefaults.essentialPlus,

            // Full Member
            fullMemberStarter:
              fullMemberMonthlyDefaults.starter,

            fullMemberEssential:
              fullMemberMonthlyDefaults.essential,

            fullMemberEssentialPlus:
              fullMemberMonthlyDefaults.essentialPlus,
            applicationFee: DEFAULT_APPLICATION_FEE,
            discountPercentage: 0,
            fullMemberStarterApplicationFee: DEFAULT_APPLICATION_FEE,
            fullMemberEssentialApplicationFee: DEFAULT_APPLICATION_FEE,
            fullMemberEssentialPlusApplicationFee: DEFAULT_APPLICATION_FEE,
            digitalMemberStarterApplicationFee: DEFAULT_APPLICATION_FEE,
            digitalMemberEssentialApplicationFee: DEFAULT_APPLICATION_FEE,
            digitalMemberEssentialPlusApplicationFee: DEFAULT_APPLICATION_FEE,
          }}
        >



          {/* FULL MEMBER */}



          <div className="config-heading">
            Configure Full Member Payment
          </div>

          <div className="configureFormRow" style={{ marginBottom: "12px" }}>
            <div className="configureFormCol">
              Select Plan
            </div>

            <div className="configureFormCol">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight:
                      fullMemberPaymentType ===
                        "MONTHLY"
                        ? 600
                        : 400,
                  }}
                >
                  Monthly
                </span>

                <Switch
                  checked={
                    fullMemberPaymentType ===
                    "YEARLY"
                  }
                  onChange={
                    handleFullMemberPaymentTypeChange
                  }
                />

                <span
                  style={{
                    fontWeight:
                      fullMemberPaymentType ===
                        "YEARLY"
                        ? 600
                        : 400,
                  }}
                >
                  Yearly
                </span>
              </div>
            </div>
          </div>

          {/* Full Member Starter */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Starter
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="fullMemberStarter"
                rules={priceRules(
                  "Full Member Starter"
                )}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Starter price"
                  value={
                    paymentPlans.fullMember[
                      fullMemberPaymentType ===
                        "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].starter
                  }
                  onChange={(value) =>
                    handleFullMemberPlanChange(
                      "starter",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          {/* Full Member Essential */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="fullMemberEssential"
                rules={priceRules(
                  "Full Member Essential"
                )}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Essential price"
                  value={
                    paymentPlans.fullMember[
                      fullMemberPaymentType ===
                        "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].essential
                  }
                  onChange={(value) =>
                    handleFullMemberPlanChange(
                      "essential",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          {/* Full Member Essential Plus */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Plus
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="fullMemberEssentialPlus"
                rules={priceRules(
                  "Full Member Essential Plus"
                )}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Essential Plus price"
                  value={
                    paymentPlans.fullMember[
                      fullMemberPaymentType ===
                        "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].essentialPlus
                  }
                  onChange={(value) =>
                    handleFullMemberPlanChange(
                      "essentialPlus",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Starter Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="fullMemberStarterApplicationFee"
                rules={priceRules("Starter Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Starter application fee"
                  value={memberApplicationFees.fullMember.starter}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "fullMember",
                      "starter",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="fullMemberEssentialApplicationFee"
                rules={priceRules("Essential Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Essential application fee"
                  value={memberApplicationFees.fullMember.essential}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "fullMember",
                      "essential",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Plus Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="fullMemberEssentialPlusApplicationFee"
                rules={priceRules("Essential Plus Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Essential Plus application fee"
                  value={memberApplicationFees.fullMember.essentialPlus}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "fullMember",
                      "essentialPlus",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

         

          {/* DIGITAL MEMBER */}
          <div className="horizontalLine"></div>


          <div className="config-heading">
            Configure Digital Member Payment
          </div>

          {/* Digital Member Payment Type */}

          <div className="configureFormRow" style={{ marginBottom: "12px" }}>
            <div className="configureFormCol">
              Select Plan
            </div>

            <div className="configureFormCol">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight:
                      paymentType === "MONTHLY"
                        ? 600
                        : 400,
                  }}
                >
                  Monthly
                </span>

                <Switch
                  checked={
                    paymentType === "YEARLY"
                  }
                  onChange={
                    handlePaymentTypeChange
                  }
                />

                <span
                  style={{
                    fontWeight:
                      paymentType === "YEARLY"
                        ? 600
                        : 400,
                  }}
                >
                  Yearly
                </span>
              </div>
            </div>
          </div>

          {/* Digital Member Starter */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Starter
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberStarter"
                rules={priceRules("Starter")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Starter price"
                  value={
                    paymentPlans.digitalMember[
                      paymentType === "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].starter
                  }
                  onChange={(value) =>
                    handleDigitalMemberPlanChange(
                      "starter",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          {/* Digital Member Essential */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberEssential"
                rules={priceRules("Essential")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Essential price"
                  value={
                    paymentPlans.digitalMember[
                      paymentType === "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].essential
                  }
                  onChange={(value) =>
                    handleDigitalMemberPlanChange(
                      "essential",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          {/* Digital Member Essential Plus */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Plus
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberEssentialPlus"
                rules={priceRules(
                  "Essential Plus"
                )}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter Essential Plus price"
                  value={
                    paymentPlans.digitalMember[
                      paymentType === "MONTHLY"
                        ? "monthly"
                        : "yearly"
                    ].essentialPlus
                  }
                  onChange={(value) =>
                    handleDigitalMemberPlanChange(
                      "essentialPlus",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Starter Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberStarterApplicationFee"
                rules={priceRules("Starter Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Starter application fee"
                  value={memberApplicationFees.digitalMember.starter}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "digitalMember",
                      "starter",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberEssentialApplicationFee"
                rules={priceRules("Essential Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Essential application fee"
                  value={memberApplicationFees.digitalMember.essential}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "digitalMember",
                      "essential",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Essential Plus Application Fee
            </div>
            <div className="configureFormCol">
              <Form.Item
                name="digitalMemberEssentialPlusApplicationFee"
                rules={priceRules("Essential Plus Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{ width: "100%", height: "40px" }}
                  placeholder="Enter Essential Plus application fee"
                  value={memberApplicationFees.digitalMember.essentialPlus}
                  onChange={(value) =>
                    handleMemberApplicationFeeChange(
                      "digitalMember",
                      "essentialPlus",
                      value
                    )
                  }
                />
              </Form.Item>
            </div>
          </div>

          <div className="horizontalLine"></div>

          {/* <div className="configureFormRow">
            <div className="configureFormCol">
              Application Fee
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="applicationFee"
                rules={priceRules("Application Fee")}
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter application fee"
                  value={applicationFee}
                  onChange={(value) => setApplicationFee(value)}
                />
              </Form.Item>
            </div>
          </div> */}

          <div className="configureFormRow">
            <div className="configureFormCol">
              Discount Percentage
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="discountPercentage"
                style={{ minWidth: "100%" }}
              >
                <InputNumber
                  min={0}
                  max={100}
                  {...numericInputProps}
                  style={{
                    width: "100%",
                    height: "40px",
                  }}
                  placeholder="Enter discount percentage"
                  value={discountPercent}
                  onChange={(value) => setDiscountPercent(value ?? 0)}
                />
              </Form.Item>
            </div>
          </div>

          <div className="configureFormRow">
            <div className="configureFormCol">
              Currency
            </div>

            <div className="configureFormCol">
              <Form.Item
                name="currency"
                rules={[
                  {
                    required: true,
                    message: "Please select currency.",
                  },
                ]}
                style={{ minWidth: "100%" }}
              >
                <Select
                  placeholder={
                    countryId
                      ? "Select currency"
                      : "Loading country details..."
                  }
                  showSearch
                  allowClear
                  loading={currenciesLoading}
                  disabled={!countryId || currenciesLoading}
                  optionFilterProp="label"
                  options={currencies.map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                />
              </Form.Item>
            </div>
          </div>

          <div
            className="configureFormRow"
            style={{
              justifyContent: "flex-end",
            }}
          >
            <div className="configureFieldRow">
              <div className="configureFieldCol">
                <div className="form-btn-container">
                  <Button
                    onClick={handleCancel}
                    type="button"
                    className="clear-btn"
                    style={{
                      marginRight: "10px",
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="saveBtn redBtn"
                    htmlType="submit"
                    loading={submitting}
                    disabled={fieldDisabled}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PaymentPlanConfiguration;
